import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { fmt12 } from "@/lib/time";
import { Calendar, CheckCircle2, Clock, XCircle, ChevronDown, Check } from "lucide-react";

const STATUSES = ["pending", "confirmed", "completed", "cancelled", "no_show"];

function CustomDropdown({ value, options, onChange, placeholder = "Select", className = "" }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border-2 border-amber-400/80 bg-gradient-to-b from-[#1a1a1a] via-black to-[#121212] text-amber-300 text-xs font-extrabold uppercase tracking-wider shadow-lg shadow-yellow-950/40 hover:border-amber-300 transition-all ${className}`}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown size={14} className={`transform transition-transform duration-300 text-amber-400 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-48 rounded-2xl border-2 border-amber-400/80 bg-[#0c0c0c]/98 backdrop-blur-xl shadow-2xl shadow-yellow-950/50 z-50 p-2"
          >
            <div className="max-h-56 overflow-y-auto pr-1.5 space-y-1">
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 text-xs font-extrabold uppercase tracking-wider flex items-center justify-between rounded-xl transition-colors ${
                      isSelected
                        ? "bg-amber-500/25 text-amber-300 border-l-4 border-amber-400"
                        : "text-white/80 hover:bg-white/10 hover:text-amber-300"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {isSelected && <Check size={14} className="text-amber-400" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AdminBookings() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/bookings${filter ? `?status=${filter}` : ""}`);
      const sorted = (r.data || []).sort((a, b) => {
        const timeA = a.created_at || `${a.date} ${a.time}` || "";
        const timeB = b.created_at || `${b.date} ${b.time}` || "";
        return timeB.localeCompare(timeA);
      });
      setRows(sorted);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const change = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status, payment_status: status === "completed" ? "paid" : undefined });
      toast.success(`Status updated to ${status}`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const totalCount = rows.length;
  const completedCount = rows.filter(r => r.status === "completed").length;
  const pendingCount = rows.filter(r => r.status === "pending").length;
  const cancelledCount = rows.filter(r => r.status === "cancelled" || r.status === "no_show").length;

  const filterOptions = [
    { label: "All Statuses", value: "" },
    ...STATUSES.map((s) => ({ label: s.replace("_", " "), value: s })),
  ];

  return (
    <div data-testid="admin-bookings" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1">APPOINTMENT MANAGEMENT</div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Appointments
          </h1>
        </div>

        <CustomDropdown
          value={filter}
          options={filterOptions}
          onChange={(val) => setFilter(val)}
          placeholder="All Statuses"
        />
      </motion.div>

      {/* Top Quick Summary Badges */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div className="p-5 rounded-2xl border border-amber-400/80 bg-gradient-to-b from-amber-950/50 via-black/90 to-amber-950/20 shadow-lg shadow-amber-950/20">
          <div className="flex justify-between items-center text-xs uppercase tracking-widest font-extrabold text-amber-400 mb-1">
            <span>Total Shown</span>
            <Calendar size={18} />
          </div>
          <div className="text-3xl font-heading font-extrabold text-white">{totalCount}</div>
        </div>

        <div className="p-5 rounded-2xl border border-emerald-400/80 bg-gradient-to-b from-emerald-950/50 via-black/90 to-emerald-950/20 shadow-lg shadow-emerald-950/20">
          <div className="flex justify-between items-center text-xs uppercase tracking-widest font-extrabold text-emerald-400 mb-1">
            <span>Completed</span>
            <CheckCircle2 size={18} />
          </div>
          <div className="text-3xl font-heading font-extrabold text-emerald-400">{completedCount}</div>
        </div>

        <div className="p-5 rounded-2xl border border-hgold/80 bg-gradient-to-b from-yellow-950/50 via-black/90 to-yellow-950/20 shadow-lg shadow-yellow-950/20">
          <div className="flex justify-between items-center text-xs uppercase tracking-widest font-extrabold text-amber-300 mb-1">
            <span>Pending</span>
            <Clock size={18} />
          </div>
          <div className="text-3xl font-heading font-extrabold text-amber-300">{pendingCount}</div>
        </div>

        <div className="p-5 rounded-2xl border border-red-500/80 bg-gradient-to-b from-red-950/50 via-black/90 to-red-950/20 shadow-lg shadow-red-950/20">
          <div className="flex justify-between items-center text-xs uppercase tracking-widest font-extrabold text-red-400 mb-1">
            <span>Cancelled</span>
            <XCircle size={18} />
          </div>
          <div className="text-3xl font-heading font-extrabold text-red-400">{cancelledCount}</div>
        </div>
      </motion.div>

      {/* Main Table Container */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 min-h-[400px]">
          <div className="premium-spinner mb-3"></div>
          <div className="text-xs text-emerald-400 uppercase tracking-widest font-bold animate-pulse">Loading Appointments...</div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="rounded-2xl border border-hgold/70 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 min-h-[480px] pb-36"
        >
          <div className="overflow-x-auto min-h-[450px]">
            <table className="w-full text-sm">
              <thead className="bg-black/80 border-b-2 border-white/10 text-amber-400 font-extrabold uppercase text-[11px] tracking-wider">
                <tr>
                  {["When", "Customer", "Contact", "Type", "Amount", "Status", "Actions"].map((h) => (
                    <th key={h} className="text-left px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {rows.map((b) => (
                  <tr key={b.id} className="hover:bg-white/5 transition-colors" data-testid={`booking-row-${b.id}`}>
                    <td className="px-5 py-4 text-white font-medium">
                      <div>{b.date}</div>
                      <div className="text-white/50 text-xs font-normal">{fmt12(b.time)}</div>
                    </td>
                    <td className="px-5 py-4 text-white font-bold">{b.customer_name}</td>
                    <td className="px-5 py-4 text-white/70 text-xs font-normal">
                      <div className="font-semibold text-white/90">{b.customer_phone}</div>
                      <div className="text-white/50">{b.customer_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-[10px] uppercase tracking-widest font-extrabold px-2.5 py-1 rounded-md bg-white/10 text-emerald-400 border border-emerald-400/30">
                        {b.booking_type}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-amber-400 font-bold text-base">{inr(b.amount)}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full inline-block ${
                        b.status === "completed" ? "bg-emerald-950/80 text-emerald-400 border border-emerald-400/50 shadow-sm" :
                        b.status === "cancelled" || b.status === "no_show" ? "bg-red-950/80 text-red-400 border border-red-500/50 shadow-sm" :
                        b.status === "confirmed" ? "bg-cyan-950/80 text-cyan-400 border border-cyan-400/50 shadow-sm" :
                        "bg-amber-950/80 text-amber-300 border border-amber-400/50 shadow-sm"
                      }`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <CustomDropdown
                        value=""
                        placeholder="Change Status"
                        className="py-1.5 px-3 text-[11px]"
                        options={STATUSES.map((s) => ({ label: s.replace("_", " "), value: s }))}
                        onChange={(status) => change(b.id, status)}
                      />
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-20 text-white/50 text-base font-medium">
                      No appointments found for the selected status.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
}
